import { Autocomplete, Box, TextField, Typography } from '@mui/material';

// Workaround for MUI's exactOptionalPropertyTypes incompatibility in Autocomplete renderInput.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const autocompleteField = (params: any) => params as React.ComponentProps<typeof TextField>;

export interface PersonOption {
  id: string;
  firstName?: string;
  lastName?: string;
  email?: string;
}

interface PersonAutocompleteProps {
  /** Candidate people to choose from. */
  people: PersonOption[];
  /** Currently selected person id (or '' for none). */
  value: string;
  /** Called with the chosen person id, or '' when cleared. */
  onChange: (personId: string) => void;
  label: string;
  placeholder?: string;
  /** Shown when the typed query matches nobody. */
  noOptionsText?: string;
  required?: boolean;
  disabled?: boolean;
  size?: 'small' | 'medium';
}

const fullName = (p: PersonOption): string =>
  `${p.firstName ?? ''} ${p.lastName ?? ''}`.trim() || p.email || p.id;

/**
 * Reusable, searchable person picker. Type-ahead filters by name AND email, so
 * it scales to hundreds/thousands of people where a plain <Select> dropdown
 * does not. Use anywhere a single person must be chosen (enroll, assign faculty,
 * mentor, admissions, etc).
 */
export function PersonAutocomplete({
  people,
  value,
  onChange,
  label,
  placeholder,
  noOptionsText,
  required,
  disabled,
  size = 'medium',
}: PersonAutocompleteProps): JSX.Element {
  const selected = people.find((p) => p.id === value) ?? null;

  return (
    <Autocomplete<PersonOption>
      options={people}
      value={selected}
      disabled={!!disabled}
      fullWidth
      size={size}
      autoHighlight
      onChange={(_e, option) => onChange(option ? option.id : '')}
      getOptionLabel={(p) => fullName(p)}
      isOptionEqualToValue={(o, v) => o.id === v.id}
      filterOptions={(opts, state) => {
        const q = state.inputValue.trim().toLowerCase();
        if (!q) return opts;
        return opts.filter(
          (p) => fullName(p).toLowerCase().includes(q) || (p.email ?? '').toLowerCase().includes(q),
        );
      }}
      renderOption={(props, p) => (
        <Box component="li" {...props} key={p.id}>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="body2" noWrap>
              {fullName(p)}
            </Typography>
            {p.email && (
              <Typography variant="caption" color="text.secondary" noWrap>
                {p.email}
              </Typography>
            )}
          </Box>
        </Box>
      )}
      {...(noOptionsText ? { noOptionsText } : {})}
      renderInput={(params) => (
        <TextField
          {...autocompleteField(params)}
          label={label}
          required={!!required}
          {...(placeholder ? { placeholder } : {})}
        />
      )}
    />
  );
}
